import { useState } from "react";
import ModalStateDialogComponent from "../../cubeComponent/html/modal/ModalWithState";
import ShimmerProfile from "./ShimmerProfile";
import { useSelector } from "react-redux";
import { createPortal } from "react-dom";
import UserNameTagComponent from "../../cubeComponent/html/UserNameTagComponent";
import { RootState } from "@Reducers/index";
import type { UserTagProfileData } from "@ServerTypes/accounts/accounts.schema";

interface UserProfileModalProps {
  /** `accounts._id` of the user whose profile card to show; keys into the `userTagData` slice. */
  userId: string;
  onClose: () => void;
}

const UserProfileModal = ({ userId, onClose }: Readonly<UserProfileModalProps>) => {
  const [isLoading] = useState(false);
  const usersData = useSelector((state: RootState) => state.userTagData);
  const userData = usersData[userId];
  const [selectedUserDataForProfile] = useState(userData?.data);

  const mountNode = document.getElementById("root");

  if (!mountNode) return null;

  const userProfileModalHeader = () => <div className="flex w-full justify-end cursor-pointer"></div>;

  const getUserAvatarComponent = (user: UserTagProfileData | null | undefined) => {
    // The modal body only mounts once the tag data has loaded (see the guard on the portal below), so
    // this never fires — but there is no avatar to draw without a user.
    if (!user) return null;
    return <UserNameTagComponent showAvatarOnly size="XL" id={user._id} />;
  };

  const userProfileModalContent = () => (
    <div className="items-center flex flex-col gap-2">
      {isLoading ? (
        <ShimmerProfile />
      ) : (
        <>
          {getUserAvatarComponent(selectedUserDataForProfile)}
          <div className="text-base text-gray-800 font-semibold">{selectedUserDataForProfile?.fullName}</div>
          {selectedUserDataForProfile?.designation && (
            <div className="text-sm text-gray-800">{selectedUserDataForProfile.designation}</div>
          )}
          {selectedUserDataForProfile?.organisationName && (
            <div className="text-sm text-gray-800">{selectedUserDataForProfile.organisationName}</div>
          )}
          <div className="text-sm text-gray-800">{selectedUserDataForProfile?.email}</div>
        </>
      )}
    </div>
  );

  return createPortal(
    <>
      {(selectedUserDataForProfile ?? isLoading) && (
        <ModalStateDialogComponent
          width="250"
          isVisible={!!selectedUserDataForProfile || isLoading}
          headerTitle={userProfileModalHeader()}
          modelContent={userProfileModalContent()}
          onCancel={onClose}
          zindex={9999}
        />
      )}
    </>,
    mountNode,
  );
};
export default UserProfileModal;
